import * as Header from '-/Custom/Header';
import { Link, Stack, useRouter } from 'expo-router';
import { useSubscription } from 'urql';
import * as Placeholder from '@/components/Custom/Placeholder';
import EventList from '@/components/Event/EventList';
import {
  EVENT_LIMIT,
  isNextUpcomingEventListItem,
} from '@/components/Event/EventList.utils';
import EventListItem from '@/components/Event/EventListItem';
import { EventListItemInfoFragment } from '@/components/Event/EventListItemInfo';
import {
  EventListItemTeamsFragment,
  EventListItemTeamsRsvpFragment,
} from '@/components/Event/EventListItemTeams';
import { EventListItemTimeFragment } from '@/components/Event/EventListItemTime';
import { EventRsvpPickerFragment } from '@/components/Event/EventRsvpPicker.types';
import * as EventListActions from '@/components/Event/EventScheduleActionButtons';
import ActionBar from '@/components/UI/ActionBar';
import Box from '@/components/UI/Box';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';
import useActiveTimestamp from '@/hooks/useActiveTimestamp';
import useEventListSession from '@/hooks/useEventListSession';
import useUserId from '@/hooks/useUserId';
import useVisibleListItems from '@/hooks/useVisibleListItems';
import useEventStore from '@/stores/useEventStore';
import { openDrawer } from '@/stores/useRootStore';
import { getCalendarDayFromIsoDate } from '@/utils/getCalendarDateAnchors';

const TITLE = 'Events';

const GetEventHomeListFuture = graphql(/* GraphQL */ `
  query GetEventHomeListFuture(
    $where: event_bool_exp!
    $personId: String!
    $limit: Int!
  ) {
    event_home(
      order_by: [
        { datetime: asc }
        { title: asc }
        { location: asc }
        { id: desc }
      ]
      where: $where
      limit: $limit
    ) @_cursorPagination(cursorArgument: "datetime") {
      id
      datetime
      ...EventListItemTime
      ...EventListItemTeams
      ...EventListItemTeamsRsvp
      ...EventListItemInfo
      ...EventRsvpPicker
    }
  }
`, [
  EventListItemInfoFragment,
  EventListItemTeamsFragment,
  EventListItemTeamsRsvpFragment,
  EventListItemTimeFragment,
  EventRsvpPickerFragment,
]);

const GetEventHomeListPast = graphql(/* GraphQL */ `
  query GetEventHomeListPast(
    $where: event_bool_exp!
    $personId: String!
    $limit: Int!
  ) {
    event_home(
      order_by: [
        { datetime: desc }
        { title: desc }
        { location: desc }
        { id: asc }
      ]
      where: $where
      limit: $limit
    ) @_cursorPagination(cursorArgument: "datetime") {
      id
      datetime
      ...EventListItemTime
      ...EventListItemTeams
      ...EventListItemTeamsRsvp
      ...EventListItemInfo
      ...EventRsvpPicker
    }
  }
`, [
  EventListItemInfoFragment,
  EventListItemTeamsFragment,
  EventListItemTeamsRsvpFragment,
  EventListItemTimeFragment,
  EventRsvpPickerFragment,
]);

const WatchEventHomeList = graphql(/* GraphQL */ `
  subscription WatchEventHomeList(
    $personId: String!
    $cursor: timestamptz!
    $eventIds: [uuid!]!
  ) {
    event_stream(
      batch_size: 25
      cursor: { initial_value: { updated_at: $cursor } }
      where: { id: { _in: $eventIds } }
    ) {
      ...EventListItemTime
      ...EventListItemTeams
      ...EventListItemTeamsRsvp
      ...EventListItemInfo
      ...EventRsvpPicker
    }
  }
`, [
  EventListItemTimeFragment,
  EventListItemTeamsFragment,
  EventListItemTeamsRsvpFragment,
  EventListItemInfoFragment,
  EventRsvpPickerFragment,
]);

export default function EventHome() {
  const { navigate } = useRouter();
  const personId = useUserId();

  const { getVisibleItems, visibleItemIds, onViewableItemsChanged } =
    useVisibleListItems<{
      id: string;
      datetime?: string | null;
    }>();

  const showMyTeams = useEventStore((state) => state.homeFilter.showMyTeams);

  const baseWhere = {
    ...(showMyTeams
      ? {
          roles_by_participant: {
            _has_key: personId,
          },
        }
      : {}),
  };

  const eventListSession = useEventListSession({
    pause: !personId,
    baseWhere,
    directionLimit: EVENT_LIMIT,
    fieldName: 'event_home',
    futureQuery: GetEventHomeListFuture,
    pastQuery: GetEventHomeListPast,
    selectFutureItems: (data) => data?.event_home ?? [],
    selectPastItems: (data) => data?.event_home ?? [],
    buildVariables: ({ where, limit }) => ({
      where,
      personId: personId ?? '',
      limit,
    }),
    resetScope: {
      kind: 'home',
    },
  });

  const cursor = useActiveTimestamp();

  useSubscription({
    query: WatchEventHomeList,
    variables: {
      cursor: cursor!,
      eventIds: visibleItemIds,
      personId,
    },
    pause: !cursor || eventListSession.fetching || !visibleItemIds.length,
  });

  const headerLeft = () => (
    <Header.Start>
      <Header.Button
        accessibilityLabel="Open navigation"
        testID="OpenNavigationButton"
        onPress={openDrawer}
      >
        <Icon name="menu-outline" size="3xl" />
      </Header.Button>
    </Header.Start>
  );

  const headerRight = () => (
    <Header.End>
      <Header.ButtonGroup>
        <Header.Button
          testID="create-event-button"
          onPress={() => {
            navigate({
              pathname: '/event/create',
              params: { eventType: 'game' },
            });
          }}
        >
          <Icon name="add-outline" size="3xl" />
        </Header.Button>
      </Header.ButtonGroup>
    </Header.End>
  );

  const headerTitle = () => {
    return (
      <Header.TitleContainer>
        <Header.Title>{TITLE}</Header.Title>
      </Header.TitleContainer>
    );
  };

  return (
    <Box flex={1}>
      <Stack.Screen
        options={{
          headerLeft,
          headerRight,
          title: TITLE,
          headerTitle,
          headerShadowVisible: false,
        }}
      />

      <ActionBar>
        <EventListActions.Filter
          onPress={() => {
            const firstVisibleItemDate = getVisibleItems()[0]?.datetime;

            navigate({
              pathname: '/event/action',
              params: {
                date: getCalendarDayFromIsoDate(
                  firstVisibleItemDate ||
                    eventListSession.anchorDate ||
                    undefined,
                ),
              },
            });
          }}
          showMyTeams={showMyTeams}
        />
        <Link href={{ pathname: '/event/share' }} asChild>
          <EventListActions.Share />
        </Link>
        <EventListActions.Refresh onPress={eventListSession.refresh} />
      </ActionBar>

      <Box flex>
        <EventList
          key={eventListSession.listKey}
          initialDate={eventListSession.anchorDate}
          data={eventListSession.data}
          fetching={eventListSession.fetching}
          onPressScrollButton={eventListSession.refresh}
          renderItem={({ item, data, index, extraData }) => {
            const nowMs =
              typeof extraData === 'number' ? extraData * 60000 : undefined;

            return (
              <EventListItem
                fragment={item}
                nowMs={nowMs}
                isNextUpcomingEvent={isNextUpcomingEventListItem({
                  data,
                  index,
                  nowMs,
                })}
                onPress={() => {
                  navigate({
                    pathname: '/event/[eventId]',
                    params: { eventId: item.id },
                  });
                }}
              />
            );
          }}
          divideUpcoming
          maintainVisibleContentPosition
          onViewableItemsChanged={onViewableItemsChanged}
          onEndReached={eventListSession.fetchNextPage}
          onStartReached={eventListSession.fetchPreviousPage}
          viewabilityConfig={{
            itemVisiblePercentThreshold: 80,
          }}
          ListEmptyComponent={
            <Placeholder.Container>
              <Placeholder.Icon name="calendar-outline" />
              <Placeholder.Text>
                You don't have any events yet.
              </Placeholder.Text>
              <Placeholder.Text>
                Create one using the + button above, or join a team or
                organization to get started.
              </Placeholder.Text>
            </Placeholder.Container>
          }
        />
      </Box>
    </Box>
  );
}
